import { type RestrictedAccess, type SpaceSlotKind, slotKey } from '@sivan/shared';
import { apartmentHref } from '../apartment/location';
import { AppLink } from '../ui/AppLink';
import { type SheetRow, displayValue, isCleared, rowState, usedSlots } from './apartment-draft';
import { ROW_STATE_LABELS, apartmentErrorText } from './labels';
import { Value } from './Value';

interface ApartmentCardsProps {
  readonly projectId: string;
  readonly rows: readonly SheetRow[];
  readonly canEdit: boolean;
  /** The restricted fields the reader may see: contacts (DR-185) and the note (DR-187). */
  readonly restricted: RestrictedAccess;
  readonly onEdit: (row: SheetRow) => void;
}

/** Every slot of a kind: number and basement, one line each. */
function Spaces({ row, kind }: { readonly row: SheetRow; readonly kind: SpaceSlotKind }) {
  const slots = Array.from({ length: Math.max(1, usedSlots(row, kind)) }, (_, slot) => slot);
  const lines = slots.flatMap((slot) => {
    const number = slotKey(kind, 'number', slot);
    if (isCleared(row, number)) return [{ slot, cleared: true, number: '', location: '' }];
    const value = displayValue(row, number);
    if (value === '') return [];
    return [
      {
        slot,
        cleared: false,
        number: value,
        location: displayValue(row, slotKey(kind, 'location', slot)),
      },
    ];
  });
  if (lines.length === 0) return <>—</>;
  return (
    <>
      {lines.map((line, index) => (
        <span key={line.slot} className="unit-card__space">
          {index > 0 && ' · '}
          {line.cleared ? (
            'תסתיים בשמירה'
          ) : (
            <>
              <Value text={line.number} />
              {line.location !== '' && (
                <>
                  {' (מרתף '}
                  <Value text={line.location} />)
                </>
              )}
            </>
          )}
        </span>
      ))}
    </>
  );
}

/** Phone layout: one readable card per apartment, with all its parking and storage. */
export function ApartmentCards({
  projectId,
  rows,
  canEdit,
  restricted,
  onEdit,
}: ApartmentCardsProps) {
  const visible = rows.filter((row) => rowState(row) !== 'placeholder');
  return (
    <ul className="unit-cards" aria-label="רשימת דירות">
      {visible.map((row) => {
        const state = rowState(row);
        const errors = Object.entries(row.errors);
        const title = `בנין ${displayValue(row, 'building') || '—'} · דירה ${displayValue(row, 'apartmentNumber') || 'חדשה'}`;
        return (
          <li
            key={row.key}
            className={`unit-card unit-card--${errors.length > 0 ? 'error' : state}`}
          >
            <div className="unit-card__head">
              <strong>{title}</strong>
              <span className={`chip chip--${errors.length > 0 ? 'error' : state}`}>
                {errors.length > 0 ? 'שגיאה' : ROW_STATE_LABELS[state]}
              </span>
            </div>
            <dl className="unit-card__facts">
              <div>
                <dt>קומה</dt>
                <dd>
                  {displayValue(row, 'floor') || '—'}
                  {displayValue(row, 'sourceFloorLabel') !== '' && (
                    <> (במקור: {displayValue(row, 'sourceFloorLabel')})</>
                  )}
                </dd>
              </div>
              <div>
                <dt>מס' דירה מקורי</dt>
                <dd>{displayValue(row, 'sourceNumber') || '—'}</dd>
              </div>
              <div>
                <dt>טיפוס</dt>
                <dd>{displayValue(row, 'modelType') || '—'}</dd>
              </div>
              <div>
                <dt>חניה</dt>
                <dd>
                  <Spaces row={row} kind="parking" />
                </dd>
              </div>
              <div>
                <dt>מחסן</dt>
                <dd>
                  <Spaces row={row} kind="storage" />
                </dd>
              </div>
            </dl>
            {restricted.contact === true ? (
              <dl className="unit-card__facts">
                <div>
                  <dt>שם דייר</dt>
                  <dd>{displayValue(row, 'contactName') || '—'}</dd>
                </div>
                <div>
                  <dt>טלפון</dt>
                  <dd>{displayValue(row, 'contactPhone') || '—'}</dd>
                </div>
              </dl>
            ) : (
              <p className="unit-card__meta">פרטי הדיירים: אינם זמינים לך</p>
            )}
            {errors.length > 0 && (
              <p className="field__error">
                {errors.map(([field, code]) => apartmentErrorText(field, code)).join(' · ')}
              </p>
            )}
            <div className="unit-card__actions">
              {row.unitId !== null && (
                <AppLink className="button button--small" to={apartmentHref(projectId, row.unitId)}>
                  סביבת הדירה
                  <span className="visually-hidden"> של {title}</span>
                </AppLink>
              )}
              {canEdit && (
                <button
                  type="button"
                  className="button button--small"
                  onClick={() => {
                    onEdit(row);
                  }}
                >
                  עריכה
                  <span className="visually-hidden"> של {title}</span>
                </button>
              )}
            </div>
          </li>
        );
      })}
    </ul>
  );
}
